import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { Category, Role, PaginatedResponse, SearchParams } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/date';
import { createSurfaceHoverSx } from '../../theme/interactions';

interface CategoryListProps {
  paginatedData: PaginatedResponse<Category> | null;
  loading: boolean;
  searchParams: SearchParams;
  onSearch: (searchTerm: string) => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onAdd: () => void;
}

export interface CategoryListRef {
  focusSearch: () => void;
}

const CategoryList = forwardRef<CategoryListRef, CategoryListProps>(({
  paginatedData,
  loading,
  searchParams,
  onSearch,
  onEdit,
  onDelete,
  onAdd,
}, ref) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [contextMenuItem, setContextMenuItem] = useState<Category | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }));

  const canWrite = user?.role === Role.COMPANY_ADMIN;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(event.target.value);
  };

  const handleClearSearch = () => {
    onSearch('');
  };

  const handleContextMenuClick = (event: React.MouseEvent, category: Category) => {
    if (!canWrite) return;
    event.preventDefault();
    setContextMenuItem(category);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuPosition(null);
    setContextMenuItem(null);
  };

  const handleContextMenuEdit = () => {
    if (contextMenuItem) {
      onEdit(contextMenuItem);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDelete = () => {
    if (contextMenuItem) {
      onDelete(contextMenuItem);
    }
    handleContextMenuClose();
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Categories</Typography>
            {canWrite && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
                Add Category
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              ref={searchInputRef}
              placeholder="Search categories..."
              value={searchParams.name || ''}
              onChange={handleSearchChange}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchParams.name && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : paginatedData?.content.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {searchParams.name 
                  ? 'No categories found matching your search'
                  : 'No categories found'
                }
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {paginatedData?.content.map((category) => (
                <Paper
                  key={category.id}
                  sx={theme => ({
                    p: 2,
                    cursor: canWrite ? 'context-menu' : 'default',
                    ...(canWrite ? createSurfaceHoverSx(theme) : {}),
                  })}
                  onContextMenu={(e) => handleContextMenuClick(e, category)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {category.color && (
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: category.color,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        )}
                        <Typography variant="h6">
                          {category.name}
                        </Typography>
                      </Box>
                      {category.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {category.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDate(category.createdAt, language)}
                      </Typography>
                    </Box>
                    {canWrite && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(category)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(category)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        open={contextMenuPosition !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition !== null
            ? { top: contextMenuPosition.y, left: contextMenuPosition.x }
            : undefined
        }
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextMenuDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
});

CategoryList.displayName = 'CategoryList';

export default CategoryList;
